import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import type { CommandPaletteSource } from './types';

interface CommandPaletteContextValue {
	isOpen: boolean;
	open: () => void;
	close: () => void;
	sources: ReadonlyArray<CommandPaletteSource>;
	/** Add a source for the life of the caller; returns the remover. */
	registerSource: (source: CommandPaletteSource) => () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
	null,
);

/** Cmd+K on a Mac, Ctrl+K elsewhere. */
export function isCommandPaletteShortcut(event: KeyboardEvent): boolean {
	if (event.key.toLowerCase() !== 'k' || event.altKey || event.shiftKey) {
		return false;
	}
	const isMac = /mac|iphone|ipad/i.test(navigator.userAgent);
	return isMac
		? event.metaKey && !event.ctrlKey
		: event.ctrlKey && !event.metaKey;
}

interface CommandPaletteProviderProps {
	/** Sources available from mount; modules may add more via `registerSource`. */
	sources?: ReadonlyArray<CommandPaletteSource>;
	children: React.ReactNode;
}

/**
 * PD-16044 — owns the palette's open state, its result sources and the
 * keyboard shortcut. The shortcut is a modifier combination, so it opens
 * the palette from inside a text field too; a page that needs Cmd+K for
 * itself calls `preventDefault` first and this listener stands down.
 */
export function CommandPaletteProvider({
	sources: initialSources = [],
	children,
}: CommandPaletteProviderProps): React.ReactElement {
	const [isOpen, setIsOpen] = useState(false);
	const [registered, setRegistered] = useState<
		ReadonlyArray<CommandPaletteSource>
	>([]);

	const open = useCallback((): void => {
		setIsOpen(true);
	}, []);
	const close = useCallback((): void => {
		setIsOpen(false);
	}, []);

	const registerSource = useCallback(
		(source: CommandPaletteSource): (() => void) => {
			setRegistered((prev) => [
				...prev.filter((s) => s.id !== source.id),
				source,
			]);
			return (): void => {
				setRegistered((prev) => prev.filter((s) => s !== source));
			};
		},
		[],
	);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.defaultPrevented || !isCommandPaletteShortcut(event)) {
				return;
			}
			event.preventDefault();
			setIsOpen((prev) => !prev);
		};
		window.addEventListener('keydown', onKeyDown);
		return (): void => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, []);

	const sources = useMemo(
		() =>
			[...initialSources, ...registered].sort(
				(a, b) => (a.order ?? 100) - (b.order ?? 100),
			),
		[initialSources, registered],
	);

	const value = useMemo<CommandPaletteContextValue>(
		() => ({ isOpen, open, close, sources, registerSource }),
		[isOpen, open, close, sources, registerSource],
	);

	return (
		<CommandPaletteContext.Provider value={value}>
			{children}
		</CommandPaletteContext.Provider>
	);
}

export function useCommandPalette(): CommandPaletteContextValue {
	const context = useContext(CommandPaletteContext);
	if (context == null) {
		throw new Error(
			'useCommandPalette must be used inside a CommandPaletteProvider',
		);
	}
	return context;
}

/** Null-safe variant for chrome that may render outside the provider. */
export function useOptionalCommandPalette(): CommandPaletteContextValue | null {
	return useContext(CommandPaletteContext);
}
